import { isDefined, isEmpty } from '@sgme/fp';
import { BatchSingleUnderlyingFieldComponent } from 'components/common-form/builder/form/node/field/BatchSingleUnderlyingsField/SingleUnderlyingComponent/BatchSingleUnderlyingFieldComponent';
import { UnderlyingsFieldProps } from 'components/common-form/builder/form/node/field/BatchSingleUnderlyingsField/SingleUnderlyingComponent/BatchSingleUnderlyingFieldComponent.connect';
import type { Underlying } from 'models/underlying';
import type { ForexUnderlying } from 'models/underlying/forex';
import { ChangeEvent, FC, useCallback, useMemo } from 'react';

const EMPTY_ARRAY: readonly [] = [];
type Props = UnderlyingsFieldProps & {
  onCutOffChange?(underlying: Underlying): void;
  value: ForexUnderlying | undefined;
};
export function BatchSingleUnderlyingFieldWithCutoffComponent({
  onCutOffChange,
  value,
  defaultUnderlyingCodeType,
  ...props
}: Props) {
  return (
    <BatchSingleUnderlyingFieldComponent {...props} value={value} defaultUnderlyingCodeType={defaultUnderlyingCodeType}>
      <CutOffData
        cutOff={value?.cutOff}
        cutOffs={value?.cutOffs ?? EMPTY_ARRAY}
        onCutOffChanged={cutOff => value && onCutOffChange?.({ ...value, cutOff })}
      />
    </BatchSingleUnderlyingFieldComponent>
  );
}

interface CutOffDataProps {
  cutOff?: string;
  defaultCutOff?: string;
  cutOffs: readonly string[];
  onCutOffChanged(value: string): void;
}
export const CutOffData: FC<CutOffDataProps> = ({ cutOff, cutOffs, onCutOffChanged }) => {
  const availableItems = useMemo(() => {
    if (isEmpty(cutOffs)) {
      return EMPTY_ARRAY;
    }
    return cutOffs!;
  }, [cutOffs]);

  const handleCutOffChanged = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const { value } = event.currentTarget;
      onCutOffChanged(value);
    },
    [onCutOffChanged],
  );

  if (isEmpty(availableItems)) {
    if (!isDefined(cutOff)) {
      return null;
    } else {
      return (
        <select id="underlying-cutoff-selector" className="form-select" data-e2e="underlying-cutoff-selector">
          <option key={cutOff} value={cutOff}>
            {cutOff}
          </option>
        </select>
      );
    }
  }

  if (!availableItems.includes(cutOff ?? 'undefined-cutoff')) {
    return (
      <select
        id="underlying-cutoff-selector"
        className="form-select"
        data-e2e="underlying-cutoff-selector"
        value={cutOff}
        onChange={handleCutOffChanged}
      >
        {[cutOff ?? '', ...availableItems].map(value => (
          <option key={value} value={value}>
            {value}
          </option>
        ))}
      </select>
    );
  }
  return (
    <select
      id="underlying-cutoff-selector"
      className="form-select"
      data-e2e="underlying-cutoff-selector"
      value={cutOff}
      onChange={handleCutOffChanged}
    >
      {availableItems.map(value => (
        <option key={value} value={value}>
          {value}
        </option>
      ))}
    </select>
  );
};
